import * as XLSX from "xlsx";

export function indianCurrency(n) {
    if (n === undefined || n === null) return "—";
    return "₹" + Number(n).toLocaleString("en-IN");
}

export const LIMIT_BADGE = {
    active: { label: "Active", bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
    expiring_soon: { label: "Expiring soon", bg: "#fef3c7", color: "#b45309", border: "#fde68a" },
    expired: { label: "Expired", bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" },
};

export function StatusBadge({ status }) {
    if (!status) return <span style={{ color: "#94a3b8", fontWeight: 700, fontSize: 12 }}>—</span>;
    const s = LIMIT_BADGE[status] ?? { label: status, bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1" };
    return (
        <span style={{
            fontSize: 11, fontWeight: 800, padding: "3px 10px",
            borderRadius: 20, whiteSpace: "nowrap", display: "inline-block",
            background: s.bg, color: s.color, border: `1.5px solid ${s.border}`,
        }}>
            {s.label}
        </span>
    );
}

const YES_NO_BADGE = {
    yes: { label: "Yes", bg: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
    no: { label: "No", bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" },
};

export function YesNoBadge({ value }) {
    const s = value ? YES_NO_BADGE.yes : YES_NO_BADGE.no;
    return (
        <span style={{
            fontSize: 11, fontWeight: 800, padding: "3px 10px",
            borderRadius: 20, whiteSpace: "nowrap", display: "inline-block",
            background: s.bg, color: s.color, border: `1.5px solid ${s.border}`,
        }}>
            {s.label}
        </span>
    );
}

export function formatDate(d) {
    if (!d || d === "—") return "—";
    if (/^\d{2}-\d{2}-\d{4}$/.test(d)) return d;
    if (/^\d{4}-\d{2}-\d{2}/.test(d)) {
        const [y, m, day] = d.slice(0, 10).split("-");
        return `${day}-${m}-${y}`;
    }
    return d;
}

export function formatDaysToExpiry(days) {
    if (days === null || days === undefined) return "—";
    if (days < 0) return `Expired ${Math.abs(days)}d ago`;
    if (days === 0) return "Expires today";
    return `In ${days} day${days === 1 ? "" : "s"}`;
}

export function filterAndSortAtRisk(users) {
    return (users || [])
        .filter((u) => u.ta_limit_status === "expiring_soon" || u.ta_limit_status === "expired")
        .sort((a, b) => (a.ta_limit_days_to_expiry ?? 0) - (b.ta_limit_days_to_expiry ?? 0));
}

const TEXT_COLUMNS = ["Phone", "Financier Customer ID", "Financier Virtual Account No"];

export function downloadCSV(data, filename) {
    const rows = data.map((u) => ({
        "Name": u.name || "",
        "Phone": u.phone || "",
        "State": u.state || "",
        "Credit Limit": u.tc_credit_display_limit ?? "",
        "Vehicle Limit": u.tc_vehicle_limit ?? "",
        "Agreement Date": formatDate(u.tc_agreement_date),
        "Active Loans": u.active_loan_count ?? "",
        "Utilized": u.utilized_loan_amount ?? "",
        "Setup Date": formatDate(u.ta_limit_setup_date),
        "Expiry Date": formatDate(u.ta_limit_expiry_date),
        "Days to Expiry": u.ta_limit_days_to_expiry ?? "",
        "Total TA Applied": u.total_ta_applied_count ?? "",
        "Total TA Amount Till Date": u.total_ta_amount_till_date ?? "",
        "Status": LIMIT_BADGE[u.ta_limit_status]?.label || (u.ta_limit_status || ""),
        "MT Funded Buyer": u.is_ta_mt_funded_buyer ? "Yes" : "No",
        "Physical Document Received": u.is_ta_onboarding_physical_document_received ? "Yes" : "No",
        "Financier Customer ID": u.financier_customer_id || "",
        "Financier Virtual Account No": u.financier_virtual_account_no || "",
    }));

    const headers = Object.keys(rows[0] || {
        "Name": "", "Phone": "", "State": "", "Credit Limit": "", "Vehicle Limit": "",
        "Agreement Date": "", "Active Loans": "", "Utilized": "", "Setup Date": "",
        "Expiry Date": "", "Days to Expiry": "", "Total TA Applied": "", "Total TA Amount Till Date": "",
        "Status": "", "MT Funded Buyer": "", "Physical Document Received": "",
        "Financier Customer ID": "", "Financier Virtual Account No": "",
    });

    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });

    // Keep identifier-style columns as text so Excel doesn't strip leading zeros
    const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:A1");
    headers.forEach((header, colIdx) => {
        if (!TEXT_COLUMNS.includes(header)) return;
        for (let r = range.s.r + 1; r <= range.e.r; r++) {
            const cellAddress = XLSX.utils.encode_cell({ r, c: colIdx });
            const cell = worksheet[cellAddress];
            if (cell && cell.v !== null && cell.v !== undefined) {
                cell.t = "s";
                cell.v = String(cell.v);
            }
        }
    });

    worksheet["!cols"] = headers.map((h) => ({
        wch: Math.min(Math.max(h.length, ...rows.map((r) => String(r[h] ?? "").length)) + 2, 40),
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TA Users");
    XLSX.writeFile(workbook, filename);
}

/**
 * Labelled form control shared by the TA user modals. Lifted out of
 * TAUserEditModal when TARenewLimitModal needed the same label /
 * is-invalid / invalid-feedback markup.
 */
export function Field({ label, name, type = "text", value, onChange, errors = {}, placeholder, required, className = "col-md-6", children }) {
    return (
        <div className={className}>
            <label className="form-label" htmlFor={name} style={{ fontSize: 12, fontWeight: 700 }}>
                {label}{required ? " *" : ""}
            </label>
            {children ?? (
                <input
                    id={name}
                    type={type}
                    className={`form-control ${errors[name] ? "is-invalid" : ""}`}
                    value={value}
                    onChange={(e) => onChange(name, e.target.value)}
                    placeholder={placeholder}
                    style={{ borderRadius: 8 }}
                />
            )}
            {errors[name] && <div className="invalid-feedback d-block">{errors[name]}</div>}
        </div>
    );
}
